import React, { useState } from "react";
import { Dialog, DialogContent } from "@mui/material";
import { Button } from "components/Buttons/Button";
import { ButtonColorScheme, ButtonType } from "types/ui";
import { useAppSelector } from "hooks/reduxHooks";
import { selectCurrentClinician } from "store/ClinicianStore/selectors";
import { cancelSubscription } from "repositories/coachApi";
import { enqueueSnackbar } from "notistack";
import { AuthRepository } from "repositories/AuthRepository";
import { setCurrentClinician } from "store/ClinicianStore/clinicianSlice";
import { useAppDispatch } from "hooks/reduxHooks";

type Props = {
  open: boolean;
  onClose: () => void;
  onUpgrade: () => void;
};

export const ManagePlanModal: React.FC<Props> = ({
  open,
  onClose,
  onUpgrade,
}) => {
  const [hasCanceledSubscription, setHasCanceledSubscription] = useState(false);
  const currentClinician = useAppSelector(selectCurrentClinician);
  const isSubscribed = currentClinician?.subscription?.subscribed;
  const analysesBlocked = currentClinician?.athletesBlockedAmount || 0;
  const dispatch = useAppDispatch();

  const getTitle = () => {
    if (isSubscribed && !hasCanceledSubscription) {
      return "Manage Plan";
    }

    if (hasCanceledSubscription) {
      return "Are you sure you want to cancel?";
    }

    return "Unlock Your Analyses";
  };

  const getSubtitle = () => {
    const title = `${
      currentClinician?.subscription?.creditsRanOut
        ? "You’ve reached your monthly limit of 5 free AI analyses. "
        : ""
    }${
      analysesBlocked > 0
        ? `${analysesBlocked} analyses were blocked this week.`
        : ""
    } Upgrade to unlock them for your athletes.`;
    if (!isSubscribed) {
      return title;
    }

    if (hasCanceledSubscription) {
      return "To cancel your subscription, just confirm your choice below. Your access to LLM exams and analyses features will end when billing cycle ends.";
    }

    return title;
  };

  const onCancelSubscription = async () => {
    try {
      await cancelSubscription();
      enqueueSnackbar("Subscription cancelled successfully", {
        variant: "success",
      });
      const authRepository = AuthRepository.getInstance();
      const currentClinician = await authRepository.getMe();
      dispatch(setCurrentClinician(currentClinician));
      onClose();
    } catch (error: any) {
      console.log(error.response);
      enqueueSnackbar(
        `Error: ${error.response.data.message || error.message}`,
        {
          variant: "error",
        }
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxWidth: "420px",
          margin: "auto",
        },
      }}
    >
      <DialogContent sx={{ padding: 0 }}>
        <div style={{ padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <h2
                style={{
                  marginBottom: 20,
                  fontSize: 30,
                  fontWeight: 700,
                  textAlign: "center",
                }}
              >
                {getTitle()}
              </h2>
            </div>
          </div>
          <p style={{ marginTop: 12, marginBottom: 24, color: "#6B7280" }}>
            {getSubtitle()}
          </p>

          {!hasCanceledSubscription ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Button
                title="Upgrade"
                onClick={onUpgrade}
                type={ButtonType.large}
                colorScheme={ButtonColorScheme.gradient}
                classNames="w-full"
              />
              {isSubscribed && !currentClinician?.subscription?.cancelledAt && (
                <Button
                  title="Cancel subscription"
                  onClick={() => {
                    setHasCanceledSubscription(true);
                  }}
                  type={ButtonType.large}
                  colorScheme={ButtonColorScheme.default}
                  classNames="w-full"
                />
              )}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Button
                title="Confirm"
                onClick={onCancelSubscription}
                type={ButtonType.large}
                colorScheme={ButtonColorScheme.gradient}
                classNames="w-full"
              />
              <Button
                title="Cancel"
                onClick={() => {
                  setHasCanceledSubscription(false);
                }}
                type={ButtonType.large}
                colorScheme={ButtonColorScheme.default}
                classNames="w-full"
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ManagePlanModal;
